import React, { useContext, useEffect, useRef, useState } from 'react';
import { Table, Button, Space, message, Input, TableColumnType, GetRef } from 'antd';
import { EditOutlined, DeleteOutlined, CopyOutlined, UploadOutlined, DownloadOutlined, PlusOutlined, SearchOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { fetchMyTemplates, cloneTemplate, deleteTemplate } from '../../api';
import { AuthContext } from '../../AuthContext';
import { FilterDropdownProps } from 'antd/es/table/interface';
import Highlighter from 'react-highlight-words';

type InputRef = GetRef<typeof Input>;

interface DataType {
    id: string;
    name: string;
    lastModified: string;
}

type DataIndex = keyof DataType;

const TemplatesPage = () => {
    const authContext = useContext(AuthContext);
    const { setLoggedIn } = authContext || {};
    const [myTemplates, setMyTemplates] = React.useState([] as any[]);
    const [total, setTotal] = React.useState(0);
    const [currentPage, setCurrentPage] = React.useState(1);
    const [pageSize, setPageSize] = React.useState(20);
    const [loading, setLoading] = React.useState(false);
    const [searchText, setSearchText] = useState('');
    const [searchedColumn, setSearchedColumn] = useState('');
    const [globalSearchText, setGlobalSearchText] = useState('');
    const searchInput = useRef<InputRef>(null);
    const navigate = useNavigate();
    const fileInputRef = useRef<HTMLInputElement>(null);

    async function fetchData(page: number = currentPage, limit: number = pageSize, search?: string) {
        setLoading(true);
        try {
            const searchParam = search !== undefined ? search : globalSearchText;
            const response = await fetchMyTemplates(page, limit, searchParam || undefined);
            if (response.status === 401) {
                if (setLoggedIn) {
                    setLoggedIn(false);
                }
            }
            else if (response.status !== 200) {
                console.log('Failed to fetch templates');
                return;
            }
            const result = await response.json();
            if (!result.success) {
                console.log('Failed to fetch templates');
                return;
            }
            setMyTemplates(result.data);
            setTotal(result.total || 0);
            setCurrentPage(result.page || page);
            setPageSize(result.limit || limit);
        } catch (err) {
            console.log('Failed to fetch templates');
            message.error('無法取得範本');
            setMyTemplates([]);
        } finally {
            setLoading(false);
        }
    }

    const handleSearch = (
        selectedKeys: string[],
        confirm: FilterDropdownProps['confirm'],
        dataIndex: DataIndex,
    ) => {
        confirm();
        setSearchText(selectedKeys[0]);
        setSearchedColumn(dataIndex);
        setGlobalSearchText(selectedKeys[0]);
        // Trigger fetchData with search term
        fetchData(1, pageSize, selectedKeys[0]);
    };

    const handleReset = (clearFilters: () => void) => {
        clearFilters();
        setSearchText('');
        setGlobalSearchText('');
        // Trigger fetchData without search term
        fetchData(1, pageSize, '');
    };

    const getColumnSearchProps = (dataIndex: DataIndex): TableColumnType<DataType> => ({
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
            <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
                <Input
                    ref={searchInput}
                    placeholder={`搜尋 ${dataIndex === 'name' ? '名稱' : '最後修改時間'}`}
                    value={selectedKeys[0]}
                    onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                    onPressEnter={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                    style={{ marginBottom: 8, display: 'block' }}
                />
                <Space>
                    <Button
                        type="primary"
                        onClick={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                        icon={<SearchOutlined />}
                        size="small"
                        style={{ width: 90 }}
                    >
                        搜尋
                    </Button>
                    <Button
                        onClick={() => clearFilters && handleReset(clearFilters)}
                        size="small"
                        style={{ width: 90 }}
                    >
                        重設
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => {
                            close();
                        }}
                    >
                        關閉
                    </Button>
                </Space>
            </div>
        ),
        filterIcon: (filtered: boolean) => (
            <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />
        ),
        onFilterDropdownOpenChange: (visible) => {
            if (visible) {
                setTimeout(() => (searchInput as any).current?.select(), 100);
            }
        },
        render: (text) =>
            searchedColumn === dataIndex ? (
                <Highlighter
                    highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
                    searchWords={[searchText]}
                    autoEscape
                    textToHighlight={text ? text.toString() : ''}
                />
            ) : (
                text
            ),
    });

    const columns: TableColumnType<DataType>[] = [
        {
            title: '名稱',
            dataIndex: 'name',
            key: 'name',
            sorter: (a: any, b: any) => {
                return a.name.localeCompare(b.name);
            },
            showSorterTooltip: false,
            ...getColumnSearchProps('name'),
            render: (text) => (
                <span style={{ fontWeight: '500', color: '#2c3e50' }}>{text}</span>
            ),
        },
        {
            title: '最後修改時間',
            dataIndex: 'lastModified',
            key: 'lastModified',
            sorter: (a: any, b: any) => {
                return new Date(a.lastModified).getTime() - new Date(b.lastModified).getTime();
            },
            showSorterTooltip: false,
            ...getColumnSearchProps('lastModified'),
            render: (text) => (
                <span style={{ color: '#5a6c7d' }}>{text}</span>
            ),
        },
        {
            title: '動作',
            key: 'action',
            render: (text: string, record: any) => (
                <Space size="middle">
                    <Button 
                        type="primary"
                        icon={<EditOutlined />} 
                        onClick={() => handleEdit(record.id)}
                        style={{
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            border: 'none',
                        }}
                    >
                        編輯
                    </Button>
                    <Button 
                        icon={<CopyOutlined />} 
                        onClick={() => handleCopy(record.id)}
                        style={{
                            borderColor: '#667eea',
                            color: '#667eea',
                        }}
                    >
                        複製
                    </Button>
                    <Button 
                        icon={<DeleteOutlined />} 
                        danger 
                        onClick={() => handleDelete(record.id)}
                    >
                        刪除
                    </Button>
                </Space>
            ),
        },
    ];

    useEffect(() => {
        fetchData();
    }, []);

    const handleEdit = (key: string) => {
        window.open(`/template/${key}`, '_blank');
    };

    const handleCopy = async (key: string) => {
        const template = myTemplates.find((t) => t.id === key);
        await cloneTemplate(template);
        message.success('複製成功');
        fetchData(currentPage, pageSize);
    }

    const handleDelete = async (key: string) => {
        console.log('Delete:', key);
        await deleteTemplate(key);
        message.success('刪除成功');
        fetchData(currentPage, pageSize);
    };

    const handleCreate = () => {
        window.open(`/new-form`, '_blank');
    }

    const handleDownload = () => {
        const dataStr = JSON.stringify(myTemplates, null, 2); // pretty print
        const dataUri = 'data:application/json;charset=utf-8,' + encodeURIComponent(dataStr);

        const exportFileDefaultName = 'data.json';

        let linkElement = document.createElement('a');
        linkElement.setAttribute('href', dataUri);
        linkElement.setAttribute('download', exportFileDefaultName);
        linkElement.click();
    };

    const handleUploadClick = () => {
        fileInputRef.current?.click();
    };

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = (e) => {
            const contents = e.target?.result;
            if (typeof contents === 'string') {
                const templates = JSON.parse(contents);
                console.log('handleFileChange', templates);
                // Now you have the templates from the file, you can do whatever you want with them
                // For example, you can set them to state or send them to a server
            }
        };
        reader.readAsText(file);
    };

    return (
        <div style={{ padding: '32px', maxWidth: '1400px', margin: '0 auto' }}>
            <div style={{ 
                marginBottom: '24px', 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                padding: '20px 24px',
                backgroundColor: '#ffffff',
                borderRadius: '12px',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
            }}>
                <h2 style={{ 
                    margin: 0,
                    fontSize: '26px',
                    fontWeight: '700',
                    color: '#1a1a1a',
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                }}>我的範本</h2>
                <Space size="middle">
                    <input type="file" ref={fileInputRef} style={{ display: 'none' }} onChange={handleFileChange} />
                    <Button 
                        icon={<UploadOutlined />} 
                        onClick={handleUploadClick} 
                        style={{ display: 'none' }}
                    >
                        上傳範本
                    </Button>
                    <Button 
                        icon={<DownloadOutlined />} 
                        onClick={handleDownload}
                        style={{
                            borderColor: '#667eea',
                            color: '#667eea',
                            fontWeight: '500',
                        }}
                    >
                        下載範本
                    </Button>
                    <Button 
                        type="primary" 
                        icon={<PlusOutlined />} 
                        onClick={handleCreate}
                        style={{
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            border: 'none',
                            fontWeight: '500',
                            boxShadow: '0 2px 6px rgba(102, 126, 234, 0.4)',
                        }}
                    >
                        新增範本
                    </Button>
                </Space>
            </div>
            <div style={{
                backgroundColor: '#ffffff',
                borderRadius: '12px',
                padding: '24px',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
            }}>
                <Table 
                    rowKey="id" 
                    columns={columns} 
                    dataSource={myTemplates} 
                    size="small"
                    loading={loading}
                    pagination={{
                        current: currentPage,
                        pageSize: pageSize,
                        total: total,
                        pageSizeOptions: ['10', '20', '30', '50'],
                        showSizeChanger: true,
                        showTotal: (total) => `總共 ${total} 條記錄`,
                        onChange: (page, size) => {
                            setCurrentPage(page);
                            setPageSize(size);
                            fetchData(page, size, globalSearchText);
                        },
                    }}
                />
            </div>
        </div>
    );
};

export default TemplatesPage;